import React, { useMemo } from "react";
import { InnovationProductTileVisual } from "design-system/src/Components/WidgetPart/LandingStandardMedia";
import {
  dualSlotsToBackgroundImages,
  dualSlotsToBackgroundImagesWithFallback,
  getVideoSlugsFromDual,
  resolveDualResponsiveMedia,
  type ResolvedDualResponsiveMedia,
} from "design-system/src/Components/WidgetPart/LandingStandardMedia";
import { renderLandingStandardSushiShopVideo } from "../renderLandingStandardSushiShopVideo";

/** Colonne « hero » type header : tile dual image/vidéo (InnovationProductTileVisual). */
export type UseLandingStandardHeroTileArgs = {
  prefix: string;
  legacy?: { desktopImageKey?: string; mobileImageKey?: string };
  aspectRatio: number;
  desktopImageWidth?: number;
  /** Dernier recours si aucune image dans les slots dual (ex. champ BO product_image). */
  fallbackImageId?: string;
  alt?: string;
};

export type UseLandingStandardDualMediaArgs = {
  rawParams: Record<string, unknown>;
  prefix: string;
  legacy?: { desktopImageKey?: string; mobileImageKey?: string };
  /** défaut true : copier l’ID image desktop sur mobile si mobile absent */
  fallbackMobileBackgroundFromDesktop?: boolean;
  /** Si défini : résolution dual + factory renderHeroVisual pour LandingHeader. */
  heroTile?: UseLandingStandardHeroTileArgs;
};

export function useLandingStandardDualMedia(args: UseLandingStandardDualMediaArgs) {
  const {
    rawParams,
    prefix,
    legacy,
    fallbackMobileBackgroundFromDesktop = true,
    heroTile,
  } = args;

  const dual = useMemo(
    () => resolveDualResponsiveMedia(rawParams, prefix, legacy),
    [
      rawParams,
      prefix,
      legacy?.desktopImageKey,
      legacy?.mobileImageKey,
    ]
  );

  const backgroundImageIds = useMemo(() => {
    if (fallbackMobileBackgroundFromDesktop) {
      return dualSlotsToBackgroundImagesWithFallback(dual);
    }
    return dualSlotsToBackgroundImages(dual);
  }, [dual, fallbackMobileBackgroundFromDesktop]);

  const { desktop: desktopVideoSlug, mobile: mobileVideoSlug } = useMemo(
    () => getVideoSlugsFromDual(dual),
    [dual]
  );

  const hasVideoBackground = Boolean(desktopVideoSlug || mobileVideoSlug);

  const heroDual = useMemo((): ResolvedDualResponsiveMedia | undefined => {
    if (!heroTile) return undefined;
    return resolveDualResponsiveMedia(rawParams, heroTile.prefix, heroTile.legacy);
  }, [
    rawParams,
    heroTile?.prefix,
    heroTile?.legacy?.desktopImageKey,
    heroTile?.legacy?.mobileImageKey,
  ]);

  const renderHeroVisual = useMemo((): (() => React.ReactElement) | undefined => {
    if (!heroTile) return undefined;
    const fb =
      (heroDual?.desktop?.kind === "image" && heroDual.desktop.imageId) ||
      (heroDual?.mobile?.kind === "image" && heroDual.mobile.imageId) ||
      heroTile.fallbackImageId ||
      "-1";
    const alt = heroTile.alt ?? "";
    const aspectRatio = heroTile.aspectRatio;
    const desktopImageWidth = heroTile.desktopImageWidth ?? 904;
    return () => (
      <InnovationProductTileVisual
        desktop={heroDual?.desktop}
        mobile={heroDual?.mobile}
        fallbackImageId={fb}
        alt={alt}
        aspectRatio={aspectRatio}
        desktopImageWidth={desktopImageWidth}
        videoPlayback="ambient"
        renderVideo={renderLandingStandardSushiShopVideo}
      />
    );
  }, [
    heroDual,
    heroTile?.aspectRatio,
    heroTile?.desktopImageWidth,
    heroTile?.fallbackImageId,
    heroTile?.alt,
  ]);

  return {
    dual,
    backgroundImageIds,
    desktopVideoSlug,
    mobileVideoSlug,
    hasVideoBackground,
    heroDual,
    renderHeroVisual,
  };
}
